// Avatar button in the top bar. Opens a small dropdown with the
// signed-in email and a sign-out action. Visually independent of the
// sidebar's identity block; the brief calls for both.
//
// Keyboard: button is focusable; Escape closes the menu; click-outside
// closes via a window listener (mounted only while the menu is open).

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../lib/auth/auth-context';

function initialFor(email: string | null | undefined): string {
  if (!email) return '?';
  const trimmed = email.trim();
  return trimmed.charAt(0).toUpperCase() || '?';
}

export function UserMenu(): JSX.Element {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleSignOut = async (): Promise<void> => {
    setOpen(false);
    await signOut();
  };

  const initial = initialFor(user?.email);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open user menu"
        className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-bg-soft text-xs font-semibold text-ink hover:bg-bg-soft focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {initial}
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-56 rounded-md border border-border bg-bg shadow-lg"
        >
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-xs text-ink-2" title={user?.email ?? ''}>
              {user?.email ?? 'Signed in'}
            </p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              void handleSignOut();
            }}
            className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-bg-soft"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
